import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const FileQueueList = ({ 
  files = [], 
  onRemoveFile, 
  onClearAll, 
  onRetryFile 
}) => {
  if (files?.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Icon name="FileX" size={48} className="mx-auto mb-3 opacity-50" />
        <p className="text-sm">No files uploaded yet</p>
        <p className="text-xs mt-1">Upload your supplier price lists to get started</p>
      </div>
    );
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'uploading':
        return { name: 'Loader2', className: 'animate-spin text-warning' };
      case 'completed':
        return { name: 'CheckCircle', className: 'text-success' };
      case 'error':
        return { name: 'AlertCircle', className: 'text-error' };
      case 'processing':
        return { name: 'Activity', className: 'animate-pulse text-primary' };
      default:
        return { name: 'Clock', className: 'text-muted-foreground' };
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'uploading':
        return 'Uploading...';
      case 'completed':
        return 'Ready';
      case 'error':
        return 'Failed';
      case 'processing':
        return 'Processing...';
      default:
        return 'Queued';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
  };

  const getFileTypeIcon = (fileName) => {
    const extension = fileName?.split('.')?.pop()?.toLowerCase();
    switch (extension) {
      case 'csv':
        return 'FileText';
      case 'xlsx': case'xls':
        return 'FileSpreadsheet';
      default:
        return 'File';
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground">
          Upload Queue ({files?.length})
        </h3>
        {files?.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearAll}
            iconName="Trash2"
            iconPosition="left"
            className="text-muted-foreground hover:text-error"
          >
            Clear All
          </Button>
        )}
      </div>
      {/* Desktop Table View */}
      <div className="hidden md:block">
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="grid grid-cols-12 gap-4 p-3 bg-muted text-xs font-medium text-muted-foreground border-b border-border">
            <div className="col-span-5">File Name</div>
            <div className="col-span-2">Size</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-2">Progress</div>
            <div className="col-span-1">Actions</div>
          </div>
          
          <div className="divide-y divide-border">
            {files?.map((file) => {
              const statusIcon = getStatusIcon(file?.status);
              
              return (
                <div key={file?.id} className="grid grid-cols-12 gap-4 p-3 items-center hover:bg-muted transition-colors duration-150">
                  {/* File Name */}
                  <div className="col-span-5 flex items-center space-x-3">
                    <Icon 
                      name={getFileTypeIcon(file?.name)} 
                      size={16} 
                      className="text-muted-foreground flex-shrink-0" 
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-foreground truncate" title={file?.name}>
                        {file?.name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {file?.type || 'Unknown type'}
                      </p>
                    </div>
                  </div>
                  {/* Size */}
                  <div className="col-span-2">
                    <span className="text-sm text-muted-foreground">
                      {formatFileSize(file?.size)}
                    </span>
                  </div>
                  {/* Status */}
                  <div className="col-span-2">
                    <div className="flex items-center space-x-2">
                      <Icon 
                        name={statusIcon?.name} 
                        size={14} 
                        className={statusIcon?.className}
                      />
                      <span className="text-sm text-foreground">
                        {getStatusText(file?.status)}
                      </span>
                    </div>
                    {file?.error && (
                      <p className="text-xs text-error mt-1" title={file?.error}>
                        {file?.error?.length > 30 ? `${file?.error?.substring(0, 30)}...` : file?.error}
                      </p>
                    )}
                  </div>
                  {/* Progress */}
                  <div className="col-span-2">
                    {file?.status === 'uploading' || file?.status === 'processing' ? (
                      <div className="space-y-1">
                        <div className="flex justify-between text-xs">
                          <span className="text-muted-foreground">
                            {Math.round(file?.progress || 0)}%
                          </span>
                        </div>
                        <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-primary transition-all duration-300 ease-smooth"
                            style={{ width: `${file?.progress || 0}%` }}
                          />
                        </div>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">
                        {file?.status === 'completed' ? '100%' : '-'}
                      </span>
                    )}
                  </div>
                  {/* Actions */}
                  <div className="col-span-1 flex items-center space-x-1">
                    {file?.status === 'error' && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onRetryFile(file?.id)}
                        iconName="RotateCcw"
                        className="h-6 w-6 text-muted-foreground hover:text-warning"
                        title="Retry upload"
                      />
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onRemoveFile(file?.id)}
                      iconName="X"
                      className="h-6 w-6 text-muted-foreground hover:text-error"
                      title="Remove file"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {/* Mobile Card View */}
      <div className="md:hidden space-y-3">
        {files?.map((file) => {
          const statusIcon = getStatusIcon(file?.status);
          
          return (
            <div key={file?.id} className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-3 flex-1 min-w-0">
                  <Icon 
                    name={getFileTypeIcon(file?.name)} 
                    size={20} 
                    className="text-muted-foreground flex-shrink-0" 
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-foreground truncate" title={file?.name}>
                      {file?.name}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatFileSize(file?.size)} • {file?.type || 'Unknown type'}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-1 flex-shrink-0">
                  {file?.status === 'error' && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onRetryFile(file?.id)}
                      iconName="RotateCcw"
                      className="h-8 w-8 text-muted-foreground hover:text-warning"
                    />
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onRemoveFile(file?.id)}
                    iconName="X"
                    className="h-8 w-8 text-muted-foreground hover:text-error"
                  />
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Icon 
                    name={statusIcon?.name} 
                    size={14} 
                    className={statusIcon?.className}
                  />
                  <span className="text-sm text-foreground">
                    {getStatusText(file?.status)}
                  </span>
                </div>

                {(file?.status === 'uploading' || file?.status === 'processing') && (
                  <span className="text-xs text-muted-foreground">
                    {Math.round(file?.progress || 0)}%
                  </span>
                )}
              </div>
              {(file?.status === 'uploading' || file?.status === 'processing') && (
                <div className="mt-2 w-full h-1.5 bg-muted rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-primary transition-all duration-300 ease-smooth"
                    style={{ width: `${file?.progress || 0}%` }}
                  />
                </div>
              )}
              {file?.error && (
                <div className="mt-2 p-2 bg-error bg-opacity-10 border border-error border-opacity-20 rounded text-xs text-error">
                  {file?.error}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FileQueueList;